import type { ItemType } from '@brickloop/core/schemas';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSettled } from '../lib/useSettled';
import { useColors, useItemColors } from '../queries';
import { Button, Checkbox, cn, number } from '../ui';

const swatch = (rgb: string | null) => ({
  background: rgb ? `#${rgb}` : 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 3px,#fff 3px,#fff 6px)',
});

/**
 * A filtered dropdown of colours to make children for. The colours a part comes in are asked of BrickLink each time
 * a part number settles and are not kept: choosing one and saving is what makes a child. When BrickLink cannot be
 * asked, every colour in the catalogue is offered instead.
 */
export function ColourPicker({
  itemType,
  itemNo,
  existing = [],
  selected,
  onChange,
}: {
  itemType: ItemType;
  itemNo: string;
  /** Colours that already have a child. Shown as added and fixed. */
  existing?: number[];
  selected: number[];
  onChange: (colorIds: number[]) => void;
}) {
  const colors = useColors();
  const settled = useSettled(itemNo.trim());
  const live = useItemColors(itemType, settled, itemType === 'PART' && !!settled);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const offered = live.data;
  const listed = offered?.available && offered.applicable && !offered.notOnBrickLink && offered.colors.length > 0 ? offered.colors : null;
  const waiting = itemType === 'PART' && !!itemNo.trim() && (settled !== itemNo.trim() || live.isFetching);

  const all = useMemo(() => (colors.data ?? []).filter((c) => c.id !== 0), [colors.data]);
  const byId = useMemo(() => new Map(all.map((c) => [c.id, c])), [all]);
  const shown = useMemo(() => {
    const base = listed && !showAll ? listed.map((c) => ({ id: c.id, name: c.name, rgb: c.rgb })) : all;
    const f = filter.trim().toLowerCase();
    const rows = f ? base.filter((c) => c.name.toLowerCase().includes(f)) : base;
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }, [listed, showAll, all, filter]);

  const have = new Set(existing);
  const pickable = shown.filter((c) => !have.has(c.id));
  const toggle = (id: number) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  // Choosing a colour closes the list and clears the filter, ready for the next one.
  const choose = (id: number) => {
    toggle(id);
    setOpen(false);
    setFilter('');
  };

  let note: string | null = null;
  if (itemType !== 'PART') note = 'Only parts come in colours. This item gets a single child.';
  else if (!itemNo.trim()) note = 'Enter the part number to see the colours it comes in.';
  else if (waiting) note = 'Asking BrickLink which colours it comes in…';
  else if (offered?.notOnBrickLink) note = 'BrickLink does not list this part number, so every colour is offered.';
  else if (offered && !offered.available) note = offered.message ?? 'BrickLink could not be asked, so every colour is offered.';
  else if (listed) note = `BrickLink lists ${number(listed.length)} ${listed.length === 1 ? 'colour' : 'colours'} for this part, asked just now and not kept.`;

  if (itemType !== 'PART') return <p className="text-sm text-slate-500">{note}</p>;

  return (
    <div className="space-y-2" ref={box}>
      {note && <p className="text-sm text-slate-600">{note}</p>}
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Chosen colours">
          {selected.map((id) => {
            const c = byId.get(id);
            return (
              <li key={id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 py-0.5 pr-1 pl-2 text-sm">
                <span className="size-3 rounded-full ring-1 ring-slate-300" style={swatch(c?.rgb ?? null)} aria-hidden="true" />
                {c?.name ?? id}
                <button type="button" aria-label={`Remove ${c?.name ?? id}`} className="rounded-full px-1.5 text-slate-500 hover:bg-slate-200" onClick={() => toggle(id)}>
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="relative">
        <input
          role="combobox"
          aria-expanded={open}
          aria-label="Choose colours"
          className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-sm placeholder:text-slate-400"
          placeholder={listed ? `Type to filter the ${number(listed.length)} colours BrickLink lists` : 'Type to filter colours'}
          value={filter}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setFilter(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') {
              e.preventDefault();
              if (pickable[0]) choose(pickable[0].id);
            }
          }}
        />
        {open && (
          <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-2.5 py-1.5">
              <Button small variant="secondary" disabled={pickable.length === 0} onClick={() => onChange([...new Set([...selected, ...pickable.map((c) => c.id)])])}>
                Choose all shown
              </Button>
              <Button small variant="ghost" disabled={selected.length === 0} onClick={() => onChange([])}>
                Clear
              </Button>
              {listed && <Checkbox label="Also colours BrickLink does not list" checked={showAll} onChange={setShowAll} />}
              <Button small variant="ghost" className="ml-auto" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
            <ul role="listbox" aria-multiselectable="true" className="max-h-64 overflow-y-auto py-1">
              {shown.map((c) => {
                const already = have.has(c.id);
                const on = already || selected.includes(c.id);
                return (
                  <li
                    key={c.id}
                    role="option"
                    aria-selected={on}
                    aria-disabled={already}
                    className={cn('flex items-center gap-2 px-2.5 py-1.5 text-sm', already ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50', on && 'bg-slate-50')}
                    onClick={() => !already && choose(c.id)}
                  >
                    <span className="w-4 text-center text-slate-700">{on ? '✓' : ''}</span>
                    <span className="size-3.5 shrink-0 rounded-full ring-1 ring-slate-300" style={swatch(c.rgb)} aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                    {already && <span className="text-xs text-slate-500">added</span>}
                  </li>
                );
              })}
              {shown.length === 0 && !waiting && <li className="px-2.5 py-3 text-sm text-slate-500">No colours match.</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
